// src/components/CollectionConcentration.jsx
// Analyse de concentration d'une collection (note ARTDAI §Priorité 5), scopée à la
// collection. Par artiste / médium, poids des 5 œuvres principales, part sans valorisation
// récente, part assurée, répartition par confiance. PAS de comparaison boursière (ARTKIV
// traite d'une collection culturelle et patrimoniale, pas d'un produit financier).
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { getMediumCategoryLabelI18n } from "../utils/artkivLabels";

const GOLD = "#a9843f";
const BORDER = "#e5e0d8";
const STALE_MONTHS = 24;

const artworkValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_market_value ?? a?.valuation_consensus ?? a?.estimated_value ?? a?.value ?? 0) || 0;

const monthsSince = (d) => {
  if (!d) return Infinity;
  const t = new Date(d).getTime();
  if (!Number.isFinite(t)) return Infinity;
  return (Date.now() - t) / (1000 * 60 * 60 * 24 * 30.44);
};

const isInsured = (a) => {
  const s = String(a?.insurance_status || "").toLowerCase();
  return s !== "" && !["none", "non_assure", "non_assuré", "not_insured", "uninsured", "no", "false"].includes(s);
};

export default function CollectionConcentration({ collectionId, defaultCurrency = "EUR" }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState(undefined);

  useEffect(() => {
    let alive = true;
    if (!collectionId) { setRows([]); return; }
    (async () => {
      try {
        const { data } = await supabase
          .from("artworks")
          .select("id, title, artist, medium_category, current_estimated_value, valuation_market_value, valuation_consensus, estimated_value, value, last_valuation_date, valuation_confidence_score, insurance_status")
          .eq("collection_id", collectionId);
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch { if (alive) setRows([]); }
    })();
    return () => { alive = false; };
  }, [collectionId]);

  const fmt = (n) => new Intl.NumberFormat(i18n.language || "fr", { style: "currency", currency: defaultCurrency || "EUR", maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
  const pct = (x) => Math.round((x || 0) * 100) + " %";

  const stats = useMemo(() => {
    const list = (rows || []).map((a) => ({ ...a, _v: artworkValue(a) }));
    const withValue = list.filter((a) => a._v > 0);
    const total = withValue.reduce((s, a) => s + a._v, 0);
    if (list.length === 0) return null;

    const groupSum = (keyFn) => {
      const m = new Map();
      for (const a of withValue) {
        const k = keyFn(a);
        if (!k) continue;
        m.set(k, (m.get(k) || 0) + a._v);
      }
      return [...m.entries()].map(([k, v]) => ({ k, v })).sort((x, y) => y.v - x.v);
    };

    const byArtist = groupSum((a) => (a.artist || "").trim() || null).slice(0, 5);
    const byMedium = groupSum((a) => a.medium_category || null).slice(0, 5);

    const top5 = [...withValue].sort((x, y) => y._v - x._v).slice(0, 5).reduce((s, a) => s + a._v, 0);
    const top5Weight = total > 0 ? top5 / total : 0;

    const staleCount = list.filter((a) => monthsSince(a.last_valuation_date) > STALE_MONTHS).length;
    const staleShare = list.length ? staleCount / list.length : 0;

    const insuredCount = list.filter(isInsured).length;
    const insuredShare = list.length ? insuredCount / list.length : 0;

    // Répartition par confiance (si des scores existent).
    const conf = { high: 0, medium: 0, low: 0 };
    let confN = 0;
    for (const a of list) {
      const c = a.valuation_confidence_score;
      if (c == null) continue;
      confN++;
      const n = Number(c);
      if (n >= 70) conf.high++; else if (n >= 40) conf.medium++; else conf.low++;
    }

    return { total, count: list.length, byArtist, byMedium, top5Weight, staleShare, insuredShare, conf, confN };
  }, [rows]);

  if (rows === undefined || !stats) return null;
  if (stats.total === 0 && stats.count === 0) return null;

  const Bars = ({ title, items, labelFn }) => {
    if (!items || items.length === 0) return null;
    const max = items[0]?.v || 1;
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase", color: "#8a8378", marginBottom: 8 }}>{title}</div>
        {items.map((it) => (
          <div key={it.k} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#1a1a1a", marginBottom: 2 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "62%" }}>{labelFn ? labelFn(it.k) : it.k}</span>
              <span style={{ color: "#6b6459" }}>{fmt(it.v)} · {stats.total ? Math.round((it.v / stats.total) * 100) : 0}%</span>
            </div>
            <div style={{ height: 6, background: "#efece7", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.max(3, (it.v / max) * 100)}%`, background: GOLD, borderRadius: 3 }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const Kpi = ({ label, value, tone }) => (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", background: "#fff", flex: "1 1 120px" }}>
      <div style={{ fontSize: 10.5, letterSpacing: ".04em", textTransform: "uppercase", color: "#8a8378" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: tone || "#1a1a1a", fontFamily: "Georgia, serif" }}>{value}</div>
    </div>
  );

  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, background: "#faf9f7", marginBottom: 24 }}>
      <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: GOLD, marginBottom: 2 }}>
        {t("concentration.eyebrow", { defaultValue: "Analyse" })}
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "Georgia, serif" }}>
        {t("concentration.title", { defaultValue: "Concentration de la collection" })}
      </h3>
      <p style={{ fontSize: 12.5, color: "#8a8378", margin: "0 0 12px" }}>
        {t("concentration.subtitle", { defaultValue: "Répartition de la valeur — repère patrimonial, pas un indice financier." })}
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Kpi label={t("concentration.top5", { defaultValue: "Poids des 5 principales" })} value={pct(stats.top5Weight)} tone={stats.top5Weight > 0.6 ? "#a9843f" : "#1a1a1a"} />
        <Kpi label={t("concentration.stale", { defaultValue: "Sans valo. récente" })} value={pct(stats.staleShare)} tone={stats.staleShare > 0.5 ? "#b3261e" : "#1a1a1a"} />
        <Kpi label={t("concentration.insured", { defaultValue: "Assurées" })} value={pct(stats.insuredShare)} tone={stats.insuredShare < 0.5 ? "#b3261e" : "#2e7d32"} />
      </div>

      <Bars title={t("concentration.byArtist", { defaultValue: "Par artiste (top 5)" })} items={stats.byArtist} />
      <Bars title={t("concentration.byMedium", { defaultValue: "Par médium (top 5)" })} items={stats.byMedium} labelFn={(k) => getMediumCategoryLabelI18n(k, t) || k} />

      {stats.confN > 0 && (
        <div style={{ marginTop: 14, fontSize: 12.5, color: "#6b6459" }}>
          {t("concentration.confidence", { defaultValue: "Confiance des valeurs" })} :{" "}
          <span style={{ color: "#2e7d32" }}>{stats.conf.high} {t("concentration.confHigh", { defaultValue: "élevée" })}</span> ·{" "}
          <span style={{ color: GOLD }}>{stats.conf.medium} {t("concentration.confMedium", { defaultValue: "moyenne" })}</span> ·{" "}
          <span style={{ color: "#b3261e" }}>{stats.conf.low} {t("concentration.confLow", { defaultValue: "faible" })}</span>
          <span style={{ color: "#b8b1a4" }}> ({stats.confN}/{stats.count})</span>
        </div>
      )}
    </section>
  );
}
